import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  container: {
    marginBottom: 15,
  },
  competencyGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
    padding: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 6,
    border: '1 solid #e0e0e0',
  },
  phaseGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginRight: 12,
    marginBottom: 8,
  },
  competencyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 2,
    marginBottom: 2,
    border: '0.5 solid #ffffff',
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
    padding: 6,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1 solid #e0e0e0'
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
    marginBottom: 4,
  },
  legendColor: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 3,
  },
  legendText: {
    fontSize: 6,
    fontFamily: 'Poppins',
  },
  emptyMessage: {
    fontSize: 8,
    color: '#888888',
    fontFamily: 'Poppins',
    textAlign: 'center',
    padding: 10,
  }
});

const categoryColors = {
  skills: '#4CAF50',      
  knowledge: '#2196F3',   
  mindset: '#FF9800',     
  instruments: '#9C27B0', 
};

const fillColors = {
  session1Only: '#EC4899',    
  session2Only: '#06B6D4',    
  bothSessions: '#8B5CF6',    
  neither: '#bbb'          
};

const ComparisonCinemaMapPDF = ({ allCompetenciesData = [], session1Responses = [], session2Responses = [] }) => {
  // Get competency IDs that were selected in each session
  const session1CompetencyIds = new Set(session1Responses?.map(response => response.competency_id) || []);
  const session2CompetencyIds = new Set(session2Responses?.map(response => response.competency_id) || []);

  // Process all competencies and group by phase
  const competenciesByPhase = {};
  const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];

  // Initialize each phase
  phases.forEach(phase => {
    competenciesByPhase[phase] = [];
  });

  // Group competencies by phase
  allCompetenciesData.forEach(comp => {
    const phase = comp.abilities?.phase || 'Unknown';
    
    // Determine selection status
    const inSession1 = session1CompetencyIds.has(comp.id);
    const inSession2 = session2CompetencyIds.has(comp.id);
    
    let selectionStatus;
    if (inSession1 && inSession2) {
      selectionStatus = 'bothSessions';
    } else if (inSession1) {
      selectionStatus = 'session1Only';
    } else if (inSession2) {
      selectionStatus = 'session2Only';
    } else {
      selectionStatus = 'neither';
    }

    const competency = {
      id: comp.id,
      name: comp.name,
      category: comp.category,
      phase: phase,
      ability: comp.abilities?.name || 'Unknown',
      selectionStatus: selectionStatus,
      inSession1: inSession1,
      inSession2: inSession2
    };

    if (competenciesByPhase[phase]) {
      competenciesByPhase[phase].push(competency);
    } else {
      // Handle unknown phases
      if (!competenciesByPhase['Unknown']) competenciesByPhase['Unknown'] = [];
      competenciesByPhase['Unknown'].push(competency);
    }
  });

  if (!allCompetenciesData || allCompetenciesData.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyMessage}>
          No competencies available in the system
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Competencies grouped by phases with spacing */}
      <View style={styles.competencyGrid}>
        {Object.entries(competenciesByPhase).map(([phase, competencies]) => {
          if (!competencies || competencies.length === 0) return null;
          
          return (
            <View key={phase} style={styles.phaseGroup}>
              {competencies.map((competency, index) => {
                const categoryColor = categoryColors[competency.category] || '#565656';
                const fillColor = fillColors[competency.selectionStatus] || '#565656';
                
                return (
                  <View
                    key={`${competency.id}-${index}`}
                    style={[
                      styles.competencyDot,
                      {
                        backgroundColor: fillColor,
                        borderColor: categoryColor,
                      }
                    ]}
                  />
                );
              })}
            </View>
          );
        })}
      </View>

      {/* Compact legend */}
      <View style={styles.legend}>
        {/* Category colors */}
        {Object.entries(categoryColors).map(([category, color]) => (
          <View key={category} style={styles.legendItem}>
            <View style={[
              styles.legendColor,
              {
                backgroundColor: 'transparent',
                border: `1 solid ${color}`,
              }
            ]} />
            <Text style={styles.legendText}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </Text>
          </View>
        ))}
        
        {/* Session status indicators */}
        <View style={styles.legendItem}>
          <View style={[
            styles.legendColor,
            {
              backgroundColor: fillColors.session1Only,
              border: '0.5 solid #ccc',
            }
          ]} />
          <Text style={styles.legendText}>Session 1</Text>
        </View>
        
        <View style={styles.legendItem}>
          <View style={[
            styles.legendColor,
            {
              backgroundColor: fillColors.session2Only,
              border: '0.5 solid #ccc',
            }
          ]} />
          <Text style={styles.legendText}>Session 2</Text>
        </View>

        <View style={styles.legendItem}>
          <View style={[
            styles.legendColor,
            {
              backgroundColor: fillColors.bothSessions,
              border: '0.5 solid #ccc',
            }
          ]} />
          <Text style={styles.legendText}>Both</Text>
        </View>
        
        <View style={styles.legendItem}>
          <View style={[
            styles.legendColor,
            {
              backgroundColor: fillColors.neither,
              border: '0.5 solid #ccc',
            }
          ]} />
          <Text style={styles.legendText}>Neither</Text>
        </View>
      </View>
    </View>
  );
};

export default ComparisonCinemaMapPDF;
